import type { StateCreator } from 'zustand'

/**
 * Onde é que o módulo activo aparece em relação ao viewer 3D.
 *
 * - `bottom`: dock horizontal por baixo do viewer (3D em cima, módulo
 *   em baixo). É o modo por omissão: o 3D continua vivo e visível.
 * - `side`: dock vertical à direita do viewer. Mesmo princípio.
 * - `floating`: painel flutuante por cima da página, arrastável e
 *   redimensionável. O viewer fica inteiro por baixo.
 * - `window`: o módulo vai para uma janela real do browser (portal); a
 *   página principal mostra o viewer inteiro.
 * - `full`: o módulo ocupa a página toda e o viewer fica escondido
 *   (display:none, nunca desmontado). Necessário para fluxos densos
 *   como a administração.
 *
 * Guardado por módulo em localStorage: o utilizador que gosta do BCF
 * numa janela à parte e da Tabela no dock não tem de repetir a escolha.
 */
export type WorkspaceLayoutMode =
  | 'full'
  | 'side'
  | 'bottom'
  | 'window'
  | 'floating'

/** Modos em que o viewer 3D e o módulo estão ambos visíveis na página. */
export function isDockMode(mode: WorkspaceLayoutMode): boolean {
  return mode === 'bottom' || mode === 'side'
}

/** Modos em que o módulo saiu do fluxo da página (destacado). */
export function isDetachedMode(mode: WorkspaceLayoutMode): boolean {
  return mode === 'floating' || mode === 'window'
}

export interface FloatRect {
  x: number
  y: number
  w: number
  h: number
}

export const DEFAULT_FLOAT_RECT: FloatRect = { x: 120, y: 120, w: 880, h: 520 }

export interface WorkspaceLayoutSlice {
  /** Modo do módulo activo. Espelho de `workspaceModuleModes[activo]`,
   *  mantido para quem só quer ler o modo em vigor (barra de topo,
   *  ficheiro de sessão) sem saber qual é o módulo. */
  workspaceLayoutMode: WorkspaceLayoutMode
  /** Modo escolhido para cada módulo, por slug. Persistido. */
  workspaceModuleModes: Record<string, WorkspaceLayoutMode>
  /** Slug do módulo em que a rota está neste momento (null no viewer). */
  workspaceActiveModule: string | null
  /** Com o dock aberto, encolhe-o para uma tira fina. Não persiste (ver
   *  `loadCollapsed`). */
  workspaceModuleCollapsed: boolean
  /** Tamanho do dock em percentagem da área de trabalho, no eixo do
   *  modo: altura no `bottom`, largura no `side`. Limitado a [15, 85] e
   *  persistido por eixo, para que quem gosta do painel lateral largo
   *  não perca a preferência ao passar pelo dock inferior. */
  workspaceModuleSizeBottom: number
  workspaceModuleSizeSide: number
  /** Posição e tamanho do painel flutuante, em pixéis. Persistido. */
  workspaceFloatRect: FloatRect
  setWorkspaceLayoutMode: (mode: WorkspaceLayoutMode) => void
  setWorkspaceActiveModule: (slug: string | null) => void
  setWorkspaceModuleCollapsed: (collapsed: boolean) => void
  setWorkspaceModuleSizeBottom: (pct: number) => void
  setWorkspaceModuleSizeSide: (pct: number) => void
  setWorkspaceFloatRect: (rect: FloatRect) => void
}

const LEGACY_MODE_KEY = 'vn2r.workspaceLayoutMode'
const MODES_KEY = 'vn2r.workspaceModuleModes'
const COLLAPSED_KEY = 'vn2r.workspaceModuleCollapsed'
const SIZE_BOTTOM_KEY = 'vn2r.workspaceModuleSizeBottom'
const SIZE_SIDE_KEY = 'vn2r.workspaceModuleSizeSide'
const FLOAT_RECT_KEY = 'vn2r.workspaceFloatRect'

/** Sem preferência guardada, o módulo abre no dock inferior. */
export const DEFAULT_MODE: WorkspaceLayoutMode = 'bottom'

/** Chave do modo herdado da migração, usado por quem ainda não tem
 *  preferência própria. */
export const INHERITED_KEY = '*'

function isMode(v: unknown): v is WorkspaceLayoutMode {
  return (
    v === 'full' ||
    v === 'side' ||
    v === 'bottom' ||
    v === 'window' ||
    v === 'floating'
  )
}

function clampPct(n: number): number {
  if (Number.isNaN(n)) return 40
  return Math.max(15, Math.min(85, Math.round(n)))
}

/**
 * Lê o mapa de modos por módulo.
 *
 * Migração do modo global antigo: havia uma só preferência para todos
 * os módulos e, uma vez em `full`, não existia nenhum botão para voltar
 * ao dock, pelo que o valor ficava lá preso para sempre e o 3D nunca
 * mais aparecia ao lado de um módulo. Por isso o `full` legado não se
 * herda: quem estava preso volta ao dock. Os outros modos herdam-se
 * como preferência inicial de todos os módulos.
 */
export function loadModuleModes(): Record<string, WorkspaceLayoutMode> {
  try {
    const raw = localStorage.getItem(MODES_KEY)
    if (raw) {
      const parsed: unknown = JSON.parse(raw)
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        const out: Record<string, WorkspaceLayoutMode> = {}
        for (const [slug, mode] of Object.entries(parsed)) {
          if (isMode(mode)) out[slug] = mode
        }
        return out
      }
    }
  } catch {
    /* ignora */
  }
  // Sem mapa novo: consumir o valor global antigo uma única vez.
  try {
    const legacy = localStorage.getItem(LEGACY_MODE_KEY)
    localStorage.removeItem(LEGACY_MODE_KEY)
    if (isMode(legacy) && legacy !== 'full') {
      return { [INHERITED_KEY]: legacy }
    }
  } catch {
    /* ignora */
  }
  return {}
}

/** Modo em vigor para um módulo: o dele, senão a preferência herdada da
 *  migração, senão o dock inferior. */
export function modeForModule(
  modes: Record<string, WorkspaceLayoutMode>,
  slug: string | null,
): WorkspaceLayoutMode {
  if (slug && modes[slug]) return modes[slug]
  return modes[INHERITED_KEY] ?? DEFAULT_MODE
}

function persistModes(modes: Record<string, WorkspaceLayoutMode>) {
  try {
    localStorage.setItem(MODES_KEY, JSON.stringify(modes))
  } catch {
    /* ignora */
  }
}

function loadSize(key: string, fallback: number): number {
  try {
    const raw = localStorage.getItem(key)
    if (raw === null) return fallback
    return clampPct(Number(raw))
  } catch {
    return fallback
  }
}

/** Mantém o painel flutuante dentro do ecrã e acima de um tamanho
 *  utilizável. Um painel guardado num monitor que já não existe voltava
 *  fora de vista e parecia que o botão não fazia nada. */
export function clampFloatRect(
  rect: FloatRect,
  viewport?: { w: number; h: number },
): FloatRect {
  const vw =
    viewport?.w ?? (typeof window === 'undefined' ? 1440 : window.innerWidth)
  const vh =
    viewport?.h ?? (typeof window === 'undefined' ? 900 : window.innerHeight)
  const w = Math.max(320, Math.min(Math.round(rect.w), vw))
  const h = Math.max(200, Math.min(Math.round(rect.h), vh))
  const x = Math.max(0, Math.min(Math.round(rect.x), vw - w))
  const y = Math.max(0, Math.min(Math.round(rect.y), vh - h))
  return { x, y, w, h }
}

export function loadFloatRect(): FloatRect {
  try {
    const raw = localStorage.getItem(FLOAT_RECT_KEY)
    if (!raw) return DEFAULT_FLOAT_RECT
    const parsed: unknown = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') return DEFAULT_FLOAT_RECT
    const r = parsed as Partial<FloatRect>
    if (
      typeof r.x !== 'number' ||
      typeof r.y !== 'number' ||
      typeof r.w !== 'number' ||
      typeof r.h !== 'number'
    ) {
      return DEFAULT_FLOAT_RECT
    }
    return clampFloatRect(r as FloatRect)
  } catch {
    return DEFAULT_FLOAT_RECT
  }
}

function loadCollapsed(): boolean {
  // O estado colapsado já persistiu entre sessões, mas isso significava
  // que quem colapsasse o painel do BCF uma vez nunca mais via painel
  // nenhum num arranque limpo: clicava no separador e a página parecia
  // avariada. Arranca sempre aberto; colapsar vale só para a sessão.
  try {
    localStorage.removeItem(COLLAPSED_KEY)
  } catch {
    /* ignora */
  }
  return false
}

export const createWorkspaceLayoutSlice: StateCreator<WorkspaceLayoutSlice> = (
  set,
  get,
) => {
  const modes = loadModuleModes()
  return {
    workspaceModuleModes: modes,
    workspaceActiveModule: null,
    workspaceLayoutMode: modeForModule(modes, null),
    workspaceModuleCollapsed: loadCollapsed(),
    workspaceModuleSizeBottom: loadSize(SIZE_BOTTOM_KEY, 40),
    workspaceModuleSizeSide: loadSize(SIZE_SIDE_KEY, 50),
    workspaceFloatRect: loadFloatRect(),
    setWorkspaceActiveModule: (slug) => {
      const { workspaceActiveModule, workspaceModuleModes } = get()
      if (workspaceActiveModule === slug) return
      set({
        workspaceActiveModule: slug,
        workspaceLayoutMode: modeForModule(workspaceModuleModes, slug),
      })
    },
    setWorkspaceLayoutMode: (mode) => {
      const { workspaceActiveModule, workspaceModuleModes } = get()
      // Sem módulo activo (utilizador no viewer) a escolha vale como
      // preferência de arranque para todos os que ainda não têm uma.
      const slug = workspaceActiveModule ?? INHERITED_KEY
      const next = { ...workspaceModuleModes, [slug]: mode }
      persistModes(next)
      set({ workspaceModuleModes: next, workspaceLayoutMode: mode })
    },
    setWorkspaceModuleCollapsed: (collapsed) => {
      // De propósito sem persistir, ver `loadCollapsed`.
      set({ workspaceModuleCollapsed: collapsed })
    },
    setWorkspaceModuleSizeBottom: (pct) => {
      const clamped = clampPct(pct)
      try {
        localStorage.setItem(SIZE_BOTTOM_KEY, String(clamped))
      } catch {
        /* ignora */
      }
      set({ workspaceModuleSizeBottom: clamped })
    },
    setWorkspaceModuleSizeSide: (pct) => {
      const clamped = clampPct(pct)
      try {
        localStorage.setItem(SIZE_SIDE_KEY, String(clamped))
      } catch {
        /* ignora */
      }
      set({ workspaceModuleSizeSide: clamped })
    },
    setWorkspaceFloatRect: (rect) => {
      const clamped = clampFloatRect(rect)
      try {
        localStorage.setItem(FLOAT_RECT_KEY, JSON.stringify(clamped))
      } catch {
        /* ignora */
      }
      set({ workspaceFloatRect: clamped })
    },
  }
}
